// askSmart-ui/src/pages/TOSP/budgetPublish.ts
//
// Building the published snapshot: the whole financial state of a budget,
// captured as it stands, in one piece.
//
// ONE FUNCTION, ALL THREE PARTS. Publishing must not be able to half-succeed —
// a budget locked with its revenue frozen and its expense allocation still
// live would be worse than one not published at all, because it would look
// finished. So the snapshot is assembled completely in memory and handed back,
// and the caller writes it with the step tick in a single document write. If
// anything here throws, nothing is written and the previous published figures
// stand.

import {
  allocateLineMonths,
  calculateCamRecovery,
  effectiveLines,
  recoverableLines,
  withManagementFee,
} from "./budgetAllocation";
import { HST_RATE } from "./portal/tenantExpenses";
import {
  BILLABLE_INCOME,
  CAM_RECOVERY_ACCOUNT,
  CAM_RECOVERY_KEY,
  CAM_RECOVERY_LABEL,
  RevenueProjection,
  withCamRecovery,
} from "./revenueBudget";
import {
  changedHands,
  LeaseRent,
  PerUnitMonths,
  splitStatement,
  StatementBasis,
} from "./tenantStatement";
import {
  BudgetExpenseSnapshotLine,
  BudgetLine,
  BudgetPublishedSnapshot,
  BudgetRevenueLine,
  BudgetStatementSnapshot,
  BudgetYear,
  CategorySplit,
  Tenant,
  tenantLabel,
  Unit,
} from "./types";

const round2 = (value: number): number => Math.round(value * 100) / 100;
const zeroes = (): number[] => new Array(12).fill(0);

/**
 * Which statement line each revenue line becomes.
 *
 * The statement is the same money as the revenue budget, read from the
 * tenant's side: what the centre books as a recovery is what the tenant is
 * invoiced. Mapped explicitly rather than by naming convention, so adding a
 * revenue line doesn't silently appear on sixty statements.
 */
const STATEMENT_LINES: { key: string; from: string; label: string }[] = [
  { key: "baseRent", from: "baseRent", label: "Base Rent" },
  { key: "storageRent", from: "storageRent", label: "Storage Rent" },
  { key: "propertyTaxes", from: "realEstateTaxes", label: "Property Taxes" },
  { key: "cams", from: "camRecovery", label: "CAMS" },
  { key: "bmvaBase", from: "bmvaBasicFees", label: "BMVA Base Fee" },
  { key: "bmvaRoyalty", from: "bmvaRoyalty", label: "BMVA Royalty" },
  { key: "percentageRent", from: "percentageRent", label: "Percentage Rent" },
];

/** The key a tenant adjustment is stored under, matching the revenue panel. */
const tenantKey = (line: string, tenantId: string, month: number): string =>
  `${line}:${tenantId}:${month}`;

/** The key a LINE adjustment is stored under. */
const lineKey = (line: string, month: number): string => `${line}:${month}`;

/**
 * The revenue lines with the manual adjustments applied.
 *
 * The snapshot has to carry EFFECTIVE figures, because it is what the tenant
 * is shown and what the budget is reconciled against. The adjustments stay on
 * the budget as well, so an edited figure still reads as an edit on the admin
 * screen years later — this is the same arithmetic the panel does to display
 * them, done once and frozen.
 */
function effectiveRevenue(
  lines: BudgetRevenueLine[],
  overrides: Record<string, number>,
): BudgetRevenueLine[] {
  return lines.map((line) => {
    const contributions = line.contributions.map((row) => {
      const months = row.months.map((amount, month) => {
        const own = overrides[tenantKey(line.key, row.id, month)];
        return own === undefined ? amount : own;
      });
      return {
        ...row,
        months,
        total: round2(months.reduce((a, b) => a + b, 0)),
        // An adjusted month is a known one: somebody has stated the figure.
        unknown: (row.unknown || new Array(12).fill(false)).map(
          (was, month) =>
            was && overrides[tenantKey(line.key, row.id, month)] === undefined,
        ),
      };
    });

    // A line's month is the sum of its rows, except where a line-level
    // adjustment was set — an expected total is sometimes known when its
    // division is not.
    const months = line.months.map((projected, month) => {
      const own = overrides[lineKey(line.key, month)];
      if (own !== undefined) return own;
      if (contributions.length === 0) return projected;
      return round2(contributions.reduce((sum, row) => sum + row.months[month], 0));
    });

    return {
      ...line,
      months,
      total: round2(months.reduce((a, b) => a + b, 0)),
      contributions,
    };
  });
}

/** Every expense line's allocation, the computed management fee included. */
function expenseAllocation(
  lines: BudgetLine[],
  splits: CategorySplit[],
  units: Unit[],
  tenants: Tenant[],
  year: number,
  overrides: Record<string, number>,
  assume: Record<string, string>,
): BudgetExpenseSnapshotLine[] {
  const withFee = withManagementFee(effectiveLines(lines, overrides), splits);
  return recoverableLines(withFee, splits).map((line) => {
    const split = splits.find((candidate) => candidate.camCategory === line.camCategory);
    const allocation = allocateLineMonths(line, split, units, tenants, year, assume);
    return {
      lineId: line.id,
      description: line.description,
      code: line.code,
      camCategory: line.camCategory,
      months:
        line.months && line.months.length === 12
          ? [...line.months]
          : new Array(12).fill((line.amount || 0) / 12),
      rows: allocation.rows
        // Rows that carry nothing are noise in a record of what was charged.
        .filter((row) => row.months.some((amount) => Math.abs(amount) > 0.005))
        .map((row) => ({ id: row.id, months: [...row.months], total: row.total })),
    };
  });
}

/**
 * Everything needed to freeze the rate lines a statement shows above its table.
 *
 * Only `sqFt` and the per-unit tax rates, because those come from unit areas
 * and tax rolls rather than from the budget — the charges themselves all come
 * from the frozen revenue. The caller supplies them from the live statement
 * calculator at the moment of publication, which freezes them at the same
 * instant as everything else.
 */
export interface StatementRates {
  sqFt: number | null;
  taxes: BudgetStatementSnapshot["perSqFt"]["taxes"];
}

/**
 * One tenancy's statement, built from the frozen revenue lines.
 *
 * HST and the total are derived here exactly as the live statement derives
 * them, from the charge lines above them — so a frozen statement adds up the
 * same way the screen always has.
 */
function statementFor(
  tenant: Tenant,
  revenue: BudgetRevenueLine[],
  rates: StatementRates,
): BudgetStatementSnapshot {
  const charges: BudgetStatementSnapshot["lines"] = [];

  for (const mapping of STATEMENT_LINES) {
    const line = revenue.find((candidate) => candidate.key === mapping.from);
    const row = line?.contributions.find((candidate) => candidate.id === tenant.id);
    if (!line || !row) continue;
    // A line the tenancy carries nothing on doesn't belong on their statement:
    // a permanent row of nil reads as a charge nobody can account for.
    if (!row.months.some((amount) => Math.abs(amount) > 0.005)) continue;
    charges.push({
      key: mapping.key,
      label: mapping.label,
      months: row.months.map((amount, month) =>
        row.unknown?.[month] ? null : round2(amount),
      ),
      total: round2(row.total),
    });
  }

  const hst = zeroes();
  const total = zeroes();
  for (let month = 0; month < 12; month += 1) {
    const known = charges
      .map((line) => line.months[month])
      .filter((amount): amount is number => amount !== null);
    if (known.length === 0) continue;
    const subtotal = known.reduce((sum, amount) => sum + amount, 0);
    hst[month] = round2(subtotal * HST_RATE);
    total[month] = round2(subtotal + hst[month]);
  }

  const sum = (months: number[]) => round2(months.reduce((a, b) => a + b, 0));
  const lines = [
    ...charges,
    { key: "hst", label: "HST", months: hst, total: sum(hst) },
    { key: "total", label: "Total", months: total, total: sum(total), emphasis: true },
  ];

  const cams = lines.find((line) => line.key === "cams")?.total ?? null;

  return {
    tenantId: tenant.id,
    label: tenantLabel(tenant),
    lines,
    perSqFt: {
      sqFt: rates.sqFt,
      cams: cams === null || !rates.sqFt ? null : round2(cams / rates.sqFt),
      taxes: rates.taxes,
    },
  };
}

/**
 * One budget's CAM recovery as the 41001 revenue line, or undefined.
 *
 * DERIVED, NEVER STORED: a function of the budget's own expense lines, their
 * categories and the splits, all of which are already on the document. A
 * stored copy could disagree with them, and did — a category reassigned or a
 * lease corrected left the recovery describing an arrangement that no longer
 * existed until somebody pressed a button again.
 *
 * `overrides` is passed where the caller has edits on screen that are not yet
 * saved: 41001 is derived from the expenses, so a cost typed and not yet
 * written has to reach it or the two halves of the sheet describe different
 * arrangements.
 */
export function camRecoveryLine(
  budget: Pick<BudgetYear, "lines" | "splits" | "year" | "camOccupancy">,
  units: Unit[],
  tenants: Tenant[],
  overrides?: Record<string, number>,
): BudgetRevenueLine | undefined {
  const recovery = calculateCamRecovery(
    effectiveLines(budget.lines, overrides || {}),
    budget.splits,
    units,
    tenants,
    budget.year,
    budget.camOccupancy,
  );
  if (recovery.rows.length === 0) return undefined;

  const money = (value: number): string =>
    value.toLocaleString(undefined, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    });

  return {
    key: CAM_RECOVERY_KEY,
    label: CAM_RECOVERY_LABEL,
    qboAccount: CAM_RECOVERY_ACCOUNT,
    group: BILLABLE_INCOME,
    months: recovery.months,
    total: recovery.total,
    // The warning on the line, so cost that found no occupant to carry it is
    // read off the sheet rather than quietly lowering the recovery.
    gap:
      recovery.unallocated > 0 || recovery.notes.length > 0
        ? [
            recovery.unallocated > 0 ? `${money(recovery.unallocated)} could not be allocated.` : null,
            ...recovery.notes,
          ]
            .filter(Boolean)
            .join(" ")
        : undefined,
    contributions: recovery.rows.map((row) => ({
      id: row.id,
      label: row.label,
      months: row.months,
      total: row.total,
      // Nothing here is ever unknown: a tenancy's share is worked out from the
      // expenses and the units it held, both of which are on the budget.
      unknown: new Array(12).fill(false),
    })),
  };
}

/**
 * One tenancy's statement for a budget that has NOT been published.
 *
 * THE STATEMENT IS THE BUDGET, READ TENANCY BY TENANCY. Everything a tenant is
 * charged — rent, taxes, CAM, BMVA, percentage rent — is the budget's own
 * figure for them, including any adjustment typed on the revenue sheet. So an
 * admin previewing an unpublished year sees exactly what publishing would
 * release, through the same function that builds the published statements.
 *
 * It replaces working the statement out from the leases a second time, which
 * disagreed with the budget by design: the budget charges the tenancy that held
 * a unit when the year opened for all twelve months, while the leases know
 * about a handover in June.
 *
 * Null where the tenancy carries nothing on the budget — the two derived lines
 * are HST and the total, so a statement of only those is an empty one.
 */
export function budgetStatement(args: {
  tenant: Tenant;
  /** Only the revenue adjustments are read, which are part of the figures. */
  budget: Pick<BudgetYear, "revenueOverrides">;
  /** The live projection for the year. */
  projected: BudgetRevenueLine[];
  camRecovery?: BudgetRevenueLine;
  rates: StatementRates;
}): BudgetStatementSnapshot | null {
  const { tenant, budget, projected, camRecovery, rates } = args;
  const revenue = effectiveRevenue(
    withCamRecovery<BudgetRevenueLine>(projected, camRecovery),
    budget.revenueOverrides || {},
  );
  const statement = statementFor(tenant, revenue, rates);
  return statement.lines.length > 2 ? statement : null;
}

/**
 * Everything the statements for one budget year are built from.
 *
 * Assembled once for the whole portfolio: the per-unit CAM alone divides
 * eleven categories across ninety-odd units, and the revenue projection reads
 * every lease in the centre. Read per tenancy afterwards.
 *
 * The CAM here is the SAME function the 41001 line uses, asked to gather its
 * rows by unit instead of by tenancy — verified on the 2025 budget to tie to
 * the line to the cent, which is the property that makes a handover split
 * legitimate: the two tenancies between them bill exactly what was budgeted.
 */
export function statementBasis(args: {
  budget: Pick<
    BudgetYear,
    "year" | "lines" | "splits" | "camOccupancy" | "revenueOverrides" | "expenseOverrides"
  >;
  /** The live projection for the year, which carries the per-unit recoveries. */
  projection: Pick<RevenueProjection, "lines" | "perUnit" | "incumbents">;
  camRecovery?: BudgetRevenueLine;
  units: Unit[];
  tenants: Tenant[];
  /** Expense edits on screen but not yet saved, where there are any. */
  expenseOverrides?: Record<string, number>;
}): StatementBasis {
  const { budget, projection, camRecovery, units, tenants } = args;
  const overrides = args.expenseOverrides ?? budget.expenseOverrides ?? {};

  const cam = calculateCamRecovery(
    effectiveLines(budget.lines, overrides),
    budget.splits,
    units,
    tenants,
    budget.year,
    budget.camOccupancy,
    "unit",
  );

  // The effective rows, so a figure keyed on the revenue sheet is the figure
  // the statements show.
  const revenue = effectiveRevenue(
    withCamRecovery<BudgetRevenueLine>(projection.lines as BudgetRevenueLine[], camRecovery),
    budget.revenueOverrides || {},
  );

  // THE ROYALTY, SPREAD ACROSS THE UNITS THAT EARNED IT. BMVA charges it on a
  // tenancy's sales, not on a space, but an arrival is to be charged what the
  // budget expected of the tenancy before them — so the budgeted royalty is
  // apportioned by area across that tenancy's units, and then follows the
  // space like every other charge. A tenancy holding its units all year gets
  // its own figure back, undivided.
  const royalty = new Map<string, number[]>();
  const royaltyLine = revenue.find((line) => line.key === "bmvaRoyalty");
  const areaOf = (tenantId: string): number =>
    [...projection.incumbents.entries()]
      .filter(([, holder]) => holder === tenantId)
      .reduce((running, [unitId]) => {
        const unit = units.find((candidate) => candidate.id === unitId);
        return running + (unit?.occupancySqFt || 0);
      }, 0);
  projection.incumbents.forEach((tenantId, unitId) => {
    const row = royaltyLine?.contributions.find((candidate) => candidate.id === tenantId);
    if (!row) return;
    const unit = units.find((candidate) => candidate.id === unitId);
    const total = areaOf(tenantId);
    // No areas recorded at all: split equally rather than losing the charge.
    const share =
      total > 0
        ? (unit?.occupancySqFt || 0) / total
        : 1 / [...projection.incumbents.values()].filter((id) => id === tenantId).length;
    royalty.set(
      unitId,
      row.months.map((amount) => amount * share),
    );
  });

  return {
    year: budget.year,
    perUnit: {
      cam: new Map(cam.rows.map((row) => [row.id, row.months])),
      taxes: projection.perUnit.realEstateTaxes,
      dues: projection.perUnit.bmvaBasicFees,
      royalty,
    },
    incumbents: projection.incumbents,
    revenue,
  };
}

export class PublishIncompleteError extends Error {}

/**
 * The complete snapshot, or an exception.
 *
 * Refuses rather than publishing something hollow: a budget with no revenue
 * lines or no costed expenses isn't finished, and locking it would record
 * nothing while looking authoritative.
 */
export function buildPublishedSnapshot(args: {
  budget: BudgetYear;
  /** The live projection for the year, 41001 already merged in. */
  projected: BudgetRevenueLine[];
  camRecovery?: BudgetRevenueLine;
  units: Unit[];
  tenants: Tenant[];
  /** Per tenancy, the rates its statement prints above the table. */
  ratesFor: (tenant: Tenant) => StatementRates;
  /**
   * The per-unit basis, frozen with the rest and used for any tenancy whose
   * space changed hands inside the year. Omitted only by callers that predate
   * it, which then publish undivided statements as before.
   */
  basis?: StatementBasis;
  /** Rent from a tenancy's own lease, for an arrival the budget never saw. */
  leaseRentFor?: (tenant: Tenant) => LeaseRent;
  actor: { by?: string; byName?: string };
}): BudgetPublishedSnapshot {
  const { budget, projected, camRecovery, units, tenants, ratesFor, basis, actor } = args;

  const revenue = effectiveRevenue(
    withCamRecovery<BudgetRevenueLine>(projected, camRecovery),
    budget.revenueOverrides || {},
  );
  if (revenue.length === 0) {
    throw new PublishIncompleteError("There is no revenue to publish.");
  }

  const expense = expenseAllocation(
    budget.lines,
    budget.splits,
    units,
    tenants,
    budget.year,
    budget.expenseOverrides || {},
    budget.camOccupancy || {},
  );
  if (expense.length === 0) {
    throw new PublishIncompleteError(
      "No expense line carries a CAM category with a usable split, so there is " +
        "nothing to allocate. Check the categories before publishing.",
    );
  }

  // Only tenancies that actually appear on the revenue. A tenancy with no
  // charges has no statement, and inventing an empty one would put a tab in
  // front of a tenant with nothing behind it.
  const charged = new Set(
    revenue.flatMap((line) => line.contributions.map((row) => row.id)),
  );
  // A HANDOVER ALREADY ON RECORD IS PUBLISHED DIVIDED. Publishing in December,
  // when the leases show a space changed hands in June, must freeze what each
  // tenancy is actually charged — not the undivided year and a note to divide
  // it later. A handover recorded AFTER this is divided on read, from the
  // per-unit figures frozen below.
  const divided = (tenant: Tenant): BudgetStatementSnapshot | null => {
    if (!basis || !changedHands(tenant, budget.year, units)) return null;
    return splitStatement({
      tenant,
      basis,
      units,
      leaseRent: args.leaseRentFor?.(tenant) || { baseRent: [], storageRent: [] },
      rates: ratesFor(tenant),
    });
  };

  // An arrival the budget never charged still gets a statement, because the
  // space they took on was charged to somebody — so the roster is every
  // tenancy on the revenue plus every tenancy a division produces.
  const statements = tenants
    .map((tenant) => {
      const split = divided(tenant);
      if (split) return split;
      if (!charged.has(tenant.id)) return null;
      return statementFor(tenant, revenue, ratesFor(tenant));
    })
    .filter(
      (statement): statement is BudgetStatementSnapshot =>
        !!statement && statement.lines.length > 2,
    );

  if (statements.length === 0) {
    throw new PublishIncompleteError(
      "No tenancy has any charge on this budget, so no statement could be built.",
    );
  }

  // Every tenancy with a statement, named as it was when published — the
  // arrivals a division produced among them.
  const tenancies: Record<string, string> = {};
  tenants.forEach((tenant) => {
    if (charged.has(tenant.id) || statements.some((row) => row.tenantId === tenant.id)) {
      tenancies[tenant.id] = tenantLabel(tenant);
    }
  });

  const asRecord = (months: PerUnitMonths): Record<string, number[]> =>
    Object.fromEntries([...months.entries()].map(([unitId, values]) => [unitId, [...values]]));

  return {
    at: new Date().toISOString(),
    by: actor.by,
    byName: actor.byName,
    revenue,
    expense,
    statements,
    tenancies,
    ...(basis
      ? {
          perUnit: {
            cam: asRecord(basis.perUnit.cam),
            taxes: asRecord(basis.perUnit.taxes),
            dues: asRecord(basis.perUnit.dues),
            royalty: asRecord(basis.perUnit.royalty),
            charged: Object.fromEntries(basis.incumbents.entries()),
          },
        }
      : {}),
  };
}

/**
 * The frozen per-unit figures, back in the shape a division reads.
 *
 * What makes a handover recorded after publication divisible: the figures are
 * the published ones, and only the question of who held the space is read
 * live — which is the new fact, and the whole point.
 */
export function basisFromSnapshot(
  snapshot: Pick<BudgetPublishedSnapshot, "perUnit" | "revenue">,
  year: number,
): StatementBasis | null {
  const frozen = snapshot.perUnit;
  if (!frozen) return null;
  const asMap = (held: Record<string, number[]>): PerUnitMonths =>
    new Map(Object.entries(held).map(([unitId, months]) => [unitId, [...months]]));
  return {
    year,
    perUnit: {
      cam: asMap(frozen.cam),
      taxes: asMap(frozen.taxes),
      dues: asMap(frozen.dues),
      royalty: asMap(frozen.royalty),
    },
    incumbents: new Map(Object.entries(frozen.charged)),
    revenue: snapshot.revenue,
  };
}
